import { FIELD_LABELS_DA } from "@/api/cle-api/errors";
import type { CourseCertificateEvidenceModel } from "@/api/cle-api/courseCertificates";
import type { EvidenceItemListModel } from "@/api/cle-api/models/evidence";

import { hasTextValue } from "./validationFieldStatus";

/**
 * Which fields must carry a value before a document can be approved.
 *
 * Mirrors `approval_completeness.py` in cle-compliance-api, which is the authority: the API
 * answers 409 EVIDENCE_FIELDS_INCOMPLETE regardless of what this file thinks. The point of
 * duplicating the rule is to disable Godkend and name the missing fields, rather than letting
 * a caseworker press it and read an error afterwards. It gates *approval only* — see
 * `getReclassifyLocks` at the bottom of this file.
 *
 * Keep the two in step. If they drift, the button enables on a document the API then refuses —
 * which is exactly the class of bug this gate was written to remove: the member portal offered
 * evidence that `get_linkable_for_period` would not accept, so an indberetning failed at signing
 * with no way forward.
 *
 * Note this asks only whether a value is *present*, never whether the AI approved it. An
 * administrator may overrule a verdict they have checked themselves — a name that does not match,
 * an unsigned certificate — and that freedom is the purpose of the manual review.
 */

/** Fields with no backend rule are deliberately absent: see approval_completeness.py. */
const CERTIFICATE_REQUIRED_FIELDS: ReadonlyArray<{
  key: string;
  isPresent: (certificate: CourseCertificateEvidenceModel) => boolean;
}> = [
  {
    key: "isLegalCourseCertificate",
    isPresent: (c) => c.isLegalCourseCertificate != null,
  },
  { key: "courseProvider", isPresent: (c) => hasTextValue(c.courseProvider) },
  { key: "participantName", isPresent: (c) => hasTextValue(c.participantName) },
  { key: "courseDate", isPresent: (c) => hasTextValue(c.courseDate) },
  {
    // ADVOkurser's e-learning template states a lesson count but never a clock schedule, so
    // course_time is not extractable from it. validate_kursusbevis waives the rule for that
    // provider and so does the API's gate; requiring it here would disable the button on
    // documents the API would happily accept.
    key: "courseTime",
    isPresent: (c) =>
      c.courseTime != null || isAdvokurserProvider(c.courseProvider),
  },
  {
    // Criterion 3 accepts a signature OR the provider's logo. provider_logo fills the hole only
    // when TRUE: the AI writes False on every run, so its presence alone proves nothing.
    key: "signature",
    isPresent: (c) => c.signature != null || c.providerLogo === true,
  },
  {
    key: "courseDescription",
    isPresent: (c) => hasTextValue(c.courseDescription),
  },
];

/** Matched on the bare wordmark so every spelling resolves: "ADVOkurser", "ADVOkurser.dk", … */
export const isAdvokurserProvider = (
  courseProvider: string | null | undefined,
): boolean => (courseProvider ?? "").toLowerCase().includes("advokurser");

export const getMissingCourseCertificateFields = (
  certificate: CourseCertificateEvidenceModel,
): string[] => {
  const missing =
    certificate.numberOfLessons == null ? ["numberOfLessons"] : [];

  return [
    ...missing,
    ...CERTIFICATE_REQUIRED_FIELDS.filter(
      ({ isPresent }) => !isPresent(certificate),
    ).map(({ key }) => key),
  ];
};

/**
 * Teaching, reduction, authorship, dispensation and legal aid.
 *
 * Only the lesson count is required. Their detail fields are either NOT NULL in the schema or,
 * for teaching's description and reduction's reason, never supplied at creation — so the API does
 * not require them either.
 */
export const getMissingEvidenceFields = (
  evidence: Pick<EvidenceItemListModel, "numberOfLessons">,
): string[] => (evidence.numberOfLessons == null ? ["numberOfLessons"] : []);

export const getMissingPolicyFields = (policy: {
  insuranceCompany: string | null;
  lawyerLiabilityAmount: number | null;
  guaranteeAmount: number | null;
}): string[] =>
  [
    hasTextValue(policy.insuranceCompany) ? null : "insuranceCompany",
    policy.lawyerLiabilityAmount == null ? "lawyerLiabilityAmount" : null,
    policy.guaranteeAmount == null ? "guaranteeAmount" : null,
  ].filter((key): key is string => key !== null);

export const INCOMPLETE_FIELDS_REASON_PREFIX =
  "Dokumentet kan ikke godkendes, før disse felter er udfyldt:";

/** Said out loud, because the empty fields are usually the reason for rejecting in the first place. */
export const REJECTION_STAYS_OPEN_SENTENCE = "Du kan stadig afvise dokumentet.";

const describeMissingFields = (missingFields: string[]): string => {
  const labels = missingFields.map((key) => FIELD_LABELS_DA[key] ?? key);

  return `${INCOMPLETE_FIELDS_REASON_PREFIX} ${labels.join(", ")}. ${REJECTION_STAYS_OPEN_SENTENCE}`;
};

export type ActionLock = { disabled: boolean; reason?: string };

/**
 * The disabled state for DocumentClassificationActions — one lock per button.
 *
 * Godkend and Afvis are not locked by the same things, so they cannot share one flag:
 *
 * - **Reported**: both locked. An indberettet document cannot change status in either direction.
 * - **Missing fields**: only Godkend. The API gates approval alone (`_reclassify_evidence` calls
 *   `ensure_complete_for_approval` in the approved branch only), because a hole in an *approved*
 *   item breaks the member's indberetning downstream — `get_linkable_for_period` requires
 *   `number_of_lessons IS NOT NULL`, so the item is offered but not submittable and signing fails
 *   with status code 13. A rejected item is never linked to a period, so a hole costs nothing.
 *
 * Locking Afvis on missing fields had it backwards: the documents with empty fields are the ones
 * that need rejecting — not a course certificate at all, unreadable scan, nothing for the AI to
 * extract — and there is nothing to fill the fields in from. It forced a caseworker to invent
 * values, which the audit trail then recorded, just to reach the reject button.
 */
export const getReclassifyLocks = ({
  isReported,
  missingFields,
  reportedReason,
}: {
  isReported: boolean;
  missingFields: string[];
  reportedReason: string;
}): { approve: ActionLock; reject: ActionLock } => {
  if (isReported) {
    return {
      approve: { disabled: true, reason: reportedReason },
      reject: { disabled: true, reason: reportedReason },
    };
  }

  if (missingFields.length > 0) {
    return {
      approve: { disabled: true, reason: describeMissingFields(missingFields) },
      reject: { disabled: false },
    };
  }

  return { approve: { disabled: false }, reject: { disabled: false } };
};
